import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { toJS } from 'mobx';
import { observer } from 'mobx-react-lite';
import {
  Button,
  InputText,
  Table,
  TableBody,
  TableHeader,
  TableHeading,
  TableRow,
  Toast,
  useQueryParams,
} from '@insurancemenu/imenu-components-lib';
import { Add } from '@insurancemenu/imenu-components-lib/dist/icons';
import { useRootStore } from 'store/rootStore';
import { RouteV2Id } from 'routes/routesConfigV2';
import PlanDetailsModal from 'components/group-info/PlanDetailsModal';
import ProductDetailsModal from 'components/group-info/ProductDetailsModal';
import { ToastColorTypes } from '@insurancemenu/imenu-components-lib/dist/types';
import TemplatesModal from 'components/group-info/TemplatesModal';
import { RateSegment } from '@insurancemenu/imenu-components-lib/dist/types/productCardsTypings';
import { ModalType } from 'global/utils/ModalsHelper';
import ActionModal from 'components/common/modals/ActionModal';
import FormModal from 'components/common/modals/FormModal';
import { redirectToRoute } from 'routes/redirectToRoute';
import ProposalTableRow from './ProposalsTableRow';
import { createStyles } from './styles';

const ProposalsTable = observer((): JSX.Element => {
  const classes = createStyles();

  // Mobx store
  const {
    browsingModalsStore,
    groupProposalsTableStore: {
      currentProposal,
      showPlanDetailsModal,
      showProductDetailsModal,
      groupProposalsPerPeriod,
      loading,
      showConfirmationModal,
      confirmationModalType,
      markAsPreferred,
      closeConfirmationModal,
      setShowPlanDetailsModal,
      setShowProductDetailsModal,
      deleteProposal,
      buildProposalsList,
      updateProposalList,
    },
    enrollmentCardStore: { enrollmentTrigger },
    updateProposalModalStore: {
      loading: renameLoading,
      saveForm,
      showSaveProposalModal,
      modalType,
      periodId,
      saveBrokerCart,
      renameProposal,
      closeSaveProposalModal,
    },
    templatesModalStore: { showTemplatesModal, openTemplatesModal, closeTemplatesModal },
    domainStoreV2: { groupInitiationRedirect },
  } = useRootStore();

  // Common data
  const { groupId } = useParams<{ groupId: string }>();

  // Query parameters
  const query = useQueryParams();

  // React state
  const [toastInfo, setToastInfo] = useState({
    showToast: false,
    toastType: ToastColorTypes.SUCCESS,
    toastMessage: '',
  });

  const {
    fields: { effectiveDate, zipCode, county, state },
  } = browsingModalsStore.form;

  // React effects
  useEffect(() => {
    if (query.get('isBuildProposal')) {
      setShowPlanDetailsModal(true);
    }
  }, []);

  // React effects

  useEffect(() => {
    if (enrollmentTrigger > 0) {
      void updateProposalList(groupId);
    } else {
      buildProposalsList(groupId);
    }
  }, [enrollmentTrigger]);

  // Component actions
  function onAddNewProposal(): void {
    if (groupInitiationRedirect) {
      redirectToRoute(RouteV2Id.INITIATE_PROPOSAL, { groupId });
    } else {
      setShowPlanDetailsModal(true);
    }
  }

  async function onPlainDetailsModalAction(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    await browsingModalsStore.getProductLines();
    setShowPlanDetailsModal(false);
    setShowProductDetailsModal(true);
  }

  function goToPreviousModal(): void {
    setShowPlanDetailsModal(true);
    setShowProductDetailsModal(false);
    closeTemplatesModal();
  }

  async function onDeleteProposal(): Promise<void> {
    closeConfirmationModal();
    await deleteProposal(currentProposal.enrollmentPeriodId, currentProposal.cartId);
  }

  async function onMarkedAsPreferredProposal(): Promise<void> {
    closeConfirmationModal();
    try {
      await markAsPreferred(currentProposal.enrollmentPeriodId, currentProposal.cartId);
      setToastInfo({
        showToast: true,
        toastType: ToastColorTypes.SUCCESS,
        toastMessage: 'Quote proposed successfully.',
      });
    } catch (error) {
      setToastInfo({
        showToast: true,
        toastType: ToastColorTypes.WARNING,
        toastMessage: 'An error has occurred while attempting to propose quote.',
      });
    }
  }

  const convertedGroupProposalsPerPeriod = groupProposalsPerPeriod.map((dateObj) => toJS(dateObj)).slice();

  const onSaveTemplate = async (): Promise<void> => {
    const errorToast = {
      showToast: true,
      toastType: ToastColorTypes.WARNING,
      toastMessage: 'An error has occurred while attempting to mark proposal as favorite.',
    };

    if (saveForm.isFormValid()) {
      try {
        await saveBrokerCart();
        setToastInfo({
          showToast: true,
          toastType: ToastColorTypes.SUCCESS,
          toastMessage: `Proposal ${saveForm.fields.name.value} marked as favorite.`,
        });
      } catch (error) {
        setToastInfo(errorToast);
      } finally {
        closeSaveProposalModal();
      }
    } else setToastInfo(errorToast);
  };

  const onRenameProposal = async (): Promise<void> => {
    if (saveForm.isFormValid()) {
      await renameProposal(periodId).then(() => {
        closeSaveProposalModal();
        void updateProposalList(groupId);
      });
    }
  };

  const FormContent = (
    <InputText
      label=""
      name="name"
      value={saveForm.fields.name.value}
      onChange={saveForm.onFieldChange}
      width="30rem"
      className={classes.inputName}
      validationPassed={!saveForm.fields.name.unpassedValidationMsg}
      validationMessage={saveForm.fields.name.unpassedValidationMsg || ''}
    />
  );

  const SaveModal = (): JSX.Element => {
    switch (modalType) {
      case 'SAVE':
        return (
          <FormModal
            show={showSaveProposalModal}
            title="Save Template"
            subtitle="Please input the name you want to save the proposal template with."
            formContent={FormContent}
            showCloseButton={false}
            onClose={closeSaveProposalModal}
            onSubmit={onSaveTemplate}
            modalType={ModalType.CREATION}
            isFormValid={saveForm.isFormValid()}
            loading={renameLoading}
          />
        );
      case 'RENAME':
        return (
          <FormModal
            show={showSaveProposalModal}
            title="Rename Proposal"
            subtitle="Please input the name you want to save the proposal with."
            formContent={FormContent}
            showCloseButton={false}
            onClose={closeSaveProposalModal}
            onSubmit={onRenameProposal}
            modalType={ModalType.CREATION}
            isFormValid={saveForm.isFormValid()}
            loading={renameLoading}
          />
        );

      default:
        return <></>;
    }
  };

  const ConfirmationModal = (): JSX.Element => {
    switch (confirmationModalType) {
      case 'DELETE_PROPOSAL':
        return (
          <ActionModal
            show={showConfirmationModal}
            title="Delete Proposal"
            subtitle="Are you sure you want to delete this proposal?"
            actionButtonText="Delete Proposal"
            closeButtonText="Cancel"
            onClose={closeConfirmationModal}
            onAction={onDeleteProposal}
            modalType={ModalType.DELETE}
          />
        );
      case 'MARK_AS_PREFERRED':
        return (
          <ActionModal
            show={showConfirmationModal}
            title="Propose Quote"
            subtitle="Are you sure you want to propose this quote to the Group Admin?"
            actionButtonText="Confirm"
            closeButtonText="Cancel"
            onClose={closeConfirmationModal}
            onAction={onMarkedAsPreferredProposal}
            modalType={ModalType.NOTIFICATION}
          />
        );

      default:
        return <></>;
    }
  };

  return (
    <>
      <div className={classes.tableWrapper}>
        <Table loading={loading} totalElements={convertedGroupProposalsPerPeriod.length} className={classes.table}>
          <TableHeader className={classes.tableHeader}>
            <TableRow>
              <TableHeading className={classes.tableHeading}>Proposal</TableHeading>
              <TableHeading className={classes.tableHeading}>Effective Date</TableHeading>
              <TableHeading className={classes.tableHeading}>Product Lines</TableHeading>
              <TableHeading className={classes.tableHeading} />
              <TableHeading className={classes.tableHeading} width="8rem">
                <Button
                  type="outlined"
                  size="medium"
                  color="primary"
                  leftIconProp={<Add />}
                  onClick={onAddNewProposal}
                  className={classes.newProposalButton}
                >
                  New Proposal
                </Button>
              </TableHeading>
            </TableRow>
          </TableHeader>
          <TableBody className={classes.tableBody}>
            {convertedGroupProposalsPerPeriod.map((groupProposalPerPeriod) => (
              <ProposalTableRow
                proposal={groupProposalPerPeriod.proposal}
                enrollmentPeriod={groupProposalPerPeriod.enrollmentPeriod}
                key={groupProposalPerPeriod.proposal.cartId}
              />
            ))}
          </TableBody>
        </Table>
      </div>
      <PlanDetailsModal
        onAction={onPlainDetailsModalAction}
        show={showPlanDetailsModal}
        onClose={(): void => setShowPlanDetailsModal(false)}
      />
      <ProductDetailsModal
        show={showProductDetailsModal}
        onClose={(): void => setShowProductDetailsModal(false)}
        openPreviousForm={goToPreviousModal}
        continueWithTemplate={(rateSegment: RateSegment): void => {
          setShowProductDetailsModal(false);
          void openTemplatesModal(rateSegment, {
            effectiveDate: effectiveDate.value,
            zipCode: zipCode.value,
            county: county.value,
            state: state.value,
          });
        }}
      />
      <TemplatesModal show={showTemplatesModal} onClose={closeTemplatesModal} openPreviousForm={goToPreviousModal} />
      {ConfirmationModal()}
      {SaveModal()}
      <Toast
        show={toastInfo.showToast}
        onClose={(): void => {
          setToastInfo({ showToast: false, toastType: ToastColorTypes.SUCCESS, toastMessage: '' });
        }}
        colorType={toastInfo.toastType}
      >
        {toastInfo.toastMessage}
      </Toast>
    </>
  );
});

export default ProposalsTable;
